import type { NavBarNode } from './types';

/**
 * Whether activating `node` opens its own panel. Root entries only: below the
 * root, and while a left entry sits in the „…“ menu (its parent is then the
 * overflow node), the entry behaves like one without a panel.
 */
export function opensPanel(node: NavBarNode): boolean {
  return !!node.panel && node.parent === null && node.children.length === 0;
}

/** DOM id of a root entry's panel, for the entry's `aria-controls`. */
export function panelIdOf(node: NavBarNode): string {
  return `${node.id}-panel`;
}

/**
 * The wrapper of the entry that opens `node`'s panel. The panel is rendered
 * outside the `role="menubar"` frame (a dialog is no allowed child of a
 * menubar), so it finds its entry by the entry's `aria-controls` instead of
 * by DOM nesting. The measurement row names no panel, so it never matches.
 */
export function findPanelAnchor(panel: HTMLElement, node: NavBarNode): HTMLElement | null {
  const root = panel.parentElement ?? panel.ownerDocument;
  const id = panelIdOf(node);
  // Compared, not put into a selector: no `CSS.escape` needed (jsdom lacks it).
  const trigger = [...root.querySelectorAll<HTMLElement>('[aria-controls]')].find(
    (el) => el.getAttribute('aria-controls') === id
  );
  return trigger?.parentElement ?? null;
}

/** Focus into an open panel: its first `[autofocus]`, else the panel itself. */
export function focusPanel(panel: HTMLElement | null): void {
  (panel?.querySelector<HTMLElement>('[autofocus]') ?? panel)?.focus();
}

/**
 * Whether an Escape typed inside a panel closes it. Like NxPopover: fields in
 * the panel keep their Escape (cancel an IME composition, clear a search), and
 * so does any content that already used the key.
 */
export function escapeClosesPanel(event: KeyboardEvent): boolean {
  if (event.key !== 'Escape' || event.defaultPrevented) return false;
  if (event.isComposing || event.keyCode === 229) return false;
  const target = event.target;
  return !(
    target instanceof Element &&
    target.closest('input, textarea, [contenteditable]:not([contenteditable="false"])')
  );
}
